import React, {useEffect, useState} from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";

import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import {
	dataReloadDependants, dataRemoveFilter
} from "../../redux/data/dataActions";

import {
	appSetAlert, appSetModalContent
} from "../../redux/app/appActions";
import { axiosDelay } from "../../config/config";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Button from '@mui/material/Button';
import Input from "@mui/material/Input";
import LinearProgress from '@mui/material/LinearProgress';
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SubjectIcon from "@mui/icons-material/Subject";
import useDataSource from "../../hooks/useDataSource";
import PlusOneIcon from "@mui/icons-material/PlusOne";

const PREFIX = 'ProjectHubSetupEditClient';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    form: `${PREFIX}-form`,
    margin: `${PREFIX}-margin`,
    formEmbeddedActions: `${PREFIX}-formEmbeddedActions`,
    listContainer: `${PREFIX}-listContainer`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
		width: '100%',
		height: '100%',
		//overflow: 'hidden'
	},

    [`& .${classes.linearProgress}`]: {
		height: '1px'
	},

    [`& .${classes.card}`]: {
		height: '100%',
		width: '100%'
	},

    [`& .${classes.form}`]: {
        height: '300px'
    },

    [`& .${classes.margin}`]: {
		margin: theme.spacing(1),
		width: 'calc(100% - 16px)'
    },

    [`& .${classes.formEmbeddedActions}`]: {
        // position: 'absolute',
        // bottom: '0px',
        width: 'calc(100% - 32px)'
    },

    [`& .${classes.listContainer}`]: {
        overflow: 'auto',
        height: '350px',
        marginTop: '8px'
    }
}));

export default function ProjectHubSetupEditClient({ configuration }) {

	const dispatch = useDispatch();

	// App State
	const config = useSelector(state => state.config);
	const filters = useSelector(state => state.data.filters);
	const isThemeLight = useSelector(state => state.persist.themeLight);
	const modalContent = useSelector(state => state.app.modalContent);

    // Component State
	const dataSourceName = configuration?.dataSource?.name ? configuration?.dataSource?.name : '';
    const [modelTypesLoaded, setModelTypesLoaded] = useState(false);
    const [modelTypes, setModelTypes] = useState([]);
    const [checked, setChecked] = useState([0]);
	const [clientLoaded, setClientLoaded] = useState(false);
	const [client, setClient] = useState({});
	const dataSource = useDataSource(dataSourceName);

	// const projectIsUnique = async (name) => {
	// 	const projectExists = dataSource.data.find(d => d.clientprojname.toLowerCase() === name.toLowerCase());
	// 	if (projectExists) {
	// 		return false;
	// 	} else {
	// 		return true;
	// 	}
	// };

	const schema = yup.object().shape({
		name: yup.string().required(), //.test("Project must be unique", "Project must be unique", projectIsUnique),
		description: yup.string().required()
	});

	const resolver = yupResolver(schema);
	const { register, handleSubmit, formState, formState: { errors } } = useForm({ resolver });

    // Load up the Clients for this Project
    useEffect(() => {

        setTimeout(function () {
			 (async () => {

				// Make first two requests
				const [selectedClient] = await Promise.all([
					axios.get("{0}DBQuery?table=vwClients&where=ClientID eq {1}&db={2}".format(config.data.authentication.api, modalContent.filterValue, config.data.authentication.apiSource))
				]);

				if (selectedClient && selectedClient.status === 200) {
					const cli = [];
					for (const p of selectedClient.data) {
						cli.push({clientID: p.clientID, clientName: p.clientName, clientDesc: p.clientDescription})
					}

					setClient(cli[0]);
					setClientLoaded(true);
				}
				else {
					console.log('**ERROR**', selectedClient.statusText);

					dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));
					setClientLoaded(true);
				}
			})();

        }, JSON.parse(axiosDelay()));
    }, []);

    const handleClose = () => {
		dispatch(appSetModalContent(null));

		// -412 represents a filter applied for a click that produces a Modal (FormatUtilities.js --> FormatModalLink)
		const filterModalExists = filters.find(f => {
			return f.id.toLowerCase() === "-412"
		});
		if (filterModalExists) {
			// Remove the filter for modal because the user's intent is to get more data, not filter the app
			dispatch(dataRemoveFilter("-412"));
		}
    };

    const onSubmit = dataToPost => {
        // TODO: Any additional validation

        // Before we post get the user's ObjectID
        setTimeout(function () {

			const postBody = {
				"ClientID": client.clientID,
				"ClientName": dataToPost.name,
				"ClientDescription": dataToPost.description
			};

			const apiUrl = '{0}clientupdate?db={1}'.format(config.data.authentication.api, config.data.authentication.apiSource);

			// Here we pass a date time stamp to ensure the latest config is always loaded
			axios
				.post(apiUrl, postBody)
				.then(function (response) {

					dispatch(appSetAlert(null, '{0} was successfully updated.'.format(dataToPost.name), 'success', 10000));

					// Refresh Dependant Data Sources
					dispatch(dataReloadDependants( ["vwClientProjects", "vwClients", "vwProjectModelTypes"] ));

					// Close the dialog and signal a reload
					// -412 represents a filter applied for a click that produces a Modal (FormatUtilities.js --> FormatModalLink)
					const filterModalExists = filters.find(f => {
						return f.id.toLowerCase() === "-412"
					});
					if (filterModalExists) {
						// Remove the filter for modal because the user's intent is to get more data, not filter the app
						dispatch(dataRemoveFilter("-412"));
					}
					handleClose();

				})
				.catch(function (error) {
					console.log('**ERROR**', error);

					dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));

					// TODO: Reset state of form
				});

        }, JSON.parse(axiosDelay()));

    };

    return (
        <Root className={classes.root}>
			<div className={classes.card}>
				{!clientLoaded &&
					<LinearProgress className={classes.linearProgress}/>
				}
                <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
					{clientLoaded &&
						<React.Fragment>
							<FormControl variant="standard" className={classes.margin}>
								<InputLabel htmlFor="input-with-icon-adornment">Client ID Number</InputLabel>
								<Input
									{...register("clientID")}
									name="clientID"
									id="clientID"
									type="number"
									startAdornment={
										<InputAdornment position="start">
											<PlusOneIcon />
										</InputAdornment>
									}
									inputProps={{
										"aria-label": "Client ID"
									}}
									disabled={true}
									defaultValue={client.clientID}
								/>
								{errors.clientID && <p style={{color: 'red'}}>{errors?.clientID?.message}</p>}
							</FormControl>
							<FormControl variant="standard" className={classes.margin}>
								<InputLabel htmlFor="input-with-icon-adornment">Client Name</InputLabel>
								<Input
									{...register("name")}
									name="name"
									id="name"
									startAdornment={
										<InputAdornment position="start">
											<TextFieldsIcon />
										</InputAdornment>
									}
									inputProps={{
										"aria-label": "Name"
									}}
									defaultValue={client.clientName}
								/>
								{errors.name && <p style={{color: 'red'}}>{errors?.name?.message}</p>}
							</FormControl>
							<FormControl variant="standard" className={classes.margin}>
								<InputLabel htmlFor="input-with-icon-adornment">Project Description</InputLabel>
								<Input
									{...register("description")}
									name="description"
									id="description"
									startAdornment={
										<InputAdornment position="start">
											<SubjectIcon />
										</InputAdornment>
									}
									inputProps={{
										"aria-label": "Description"
									}}
									defaultValue={client.clientDesc}
								/>
								{errors.description && <p style={{color: 'red'}}>{errors?.description?.message}</p>}
							</FormControl>

						</React.Fragment>
					}
					<div className={classes.formEmbeddedActions}>
						<Button color={isThemeLight ? "primary" : "default"} onClick={() =>{ handleClose() }} disabled={formState.isSubmitting}>
							Cancel
						</Button>
						<Button color={isThemeLight ? "primary" : "default"} type="submit" disabled={formState.isSubmitting}>
							Submit
						</Button>
					</div>
                </form>
			</div>
		</Root>
    );
}
